import React, { FC, useRef, useState } from 'react';
import { Col, notification } from 'antd';

// components
import { TextComponent } from 'baseComponents/TextComponent';
import { InputComponent } from 'baseComponents/InputComponent';
import { ButtonComponent } from 'baseComponents/ButtonComponent';
import { SelectComponent } from 'baseComponents/SelectComponent';

// constants
import {
  PURCHASE_CREDITS_SCREEN,
  singleCreditCost,
  vatRate,
} from 'constants/enums/purchase-credits-screen';

// hooks
import { useGetCredits } from 'hooks/useGetCredits';
import {
  useCreatePaymentSession,
  CreatePaymentSessionResponse,
} from 'hooks/useCreatePaymentSession';
import { useNavigate } from 'react-router';

// helpers
import {
  loadCheckoutWebComponents,
  checkoutEnvironment,
} from 'helpers/checkout/flow';

// store
import { useUserStore } from 'store/UserStore';

// styles
import 'screens/PurchaseCreditsScreen/purchaseCreditsScreen.scss';

interface PurchaseCreditsTabProps {
  creditsQuery: ReturnType<typeof useGetCredits>;
  onLoadingChange: (loading: boolean) => void;
  onClose?: () => void;
  paymentFromCreateCampaign?: boolean;
}
export const PurchaseCreditsTab: FC<PurchaseCreditsTabProps> = ({
  creditsQuery,
  onLoadingChange,
  onClose,
  paymentFromCreateCampaign = false,
}) => {
  const [api, contextHolder] = notification.useNotification();
  const [creditsAmount, setCreditsAmount] = useState<string | null>(null);
  const [flowMounted, setFlowMounted] = useState(false);
  const navigate = useNavigate();
  const storesList = useUserStore((state) => state.stores);
  const [selectedStoreId, setSelectedStoreId] = useState<number>(
    storesList[0].id
  );
  const flowContainerRef = useRef<HTMLDivElement>(null);

  const roundStringAmount = (amountString: string): string => {
    const amount = parseFloat(amountString);
    const creditCost = amount * singleCreditCost * vatRate;
    const roundedCreditCost = Math.ceil(creditCost * 100) / 100;
    return roundedCreditCost.toFixed(2);
  };

  const notifyError = (description: string) =>
    api.error({
      message: 'Something went wrong',
      description,
      duration: 3,
    });

  const handlePaymentSuccess = () => {
    if (paymentFromCreateCampaign) {
      onClose && onClose();
      navigate('/create-campaign', { state: { redirectFromPayment: true } });
    } else {
      navigate('/purchase-credits-success');
    }
  };

  const mountFlow = async (data: CreatePaymentSessionResponse) => {
    try {
      await loadCheckoutWebComponents();
      const publicKey = process.env.REACT_APP_PUBLIC_CHECKOUT_KEY || '';
      const checkout = await (window as any).CheckoutWebComponents({
        paymentSession: data.session,
        publicKey,
        environment: checkoutEnvironment(publicKey),
        onPaymentCompleted: () => handlePaymentSuccess(),
        onError: (_component: any, error: any) => {
          onLoadingChange(false);
          notifyError(
            error?.message ||
              'Something went wrong with the payment. Please try again later.'
          );
        },
      });
      // Mount only card payments; the full Flow component also offers wallets.
      const flow = checkout.create('card', { showPayButton: true });
      if (!(await flow.isAvailable())) {
        throw new Error(
          'Card payments are currently unavailable. Please try again later.'
        );
      }
      flow.mount(flowContainerRef.current);
      setFlowMounted(true);
    } catch (e: any) {
      notifyError(
        e?.message || 'Could not load the payment form. Please try again later.'
      );
    } finally {
      onLoadingChange(false);
    }
  };

  const createPaymentSession = useCreatePaymentSession({
    onSuccess: (data) => mountFlow(data),
    onError: (r: any) => {
      onLoadingChange(false);
      notifyError(
        r?.message ||
          'Something went wrong with payment request. Please try again later.'
      );
    },
  });

  const handleContinueToPayment = () => {
    if (!creditsAmount || creditsAmount === '0') {
      return;
    }
    onLoadingChange(true);
    createPaymentSession.mutate({
      credits: parseInt(creditsAmount),
      store_id: selectedStoreId,
    });
  };

  const currentCreditsValue = () => {
    if (creditsQuery.error) {
      return PURCHASE_CREDITS_SCREEN.SOMETHING_WENT_WRONG;
    }
    return creditsQuery.data?.credits.toString() || '';
  };

  const handleCreditsAmountChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setCreditsAmount(e.target.value === 'null' ? '0' : e.target.value);
  };

  const getAmountToPay = () => {
    if (creditsAmount) {
      return `${PURCHASE_CREDITS_SCREEN.CREDIT_COST} £${roundStringAmount(
        creditsAmount
      )}`;
    }
    return `${PURCHASE_CREDITS_SCREEN.CREDIT_COST} £0.00`;
  };

  const handleStoreChange = (storeId: number): void => {
    setSelectedStoreId(storeId);
  };

  return (
    <div className='frames-content-wrapper'>
      {contextHolder}
      <Col
        lg={{ pull: 3, push: 3, span: 18 }}
        md={{ pull: 1, push: 1, span: 22 }}
      >
        <div className='frame-input-wrapper'>
          <SelectComponent
            label={PURCHASE_CREDITS_SCREEN.COMPANY_NAME}
            defaultValue={selectedStoreId}
            onSelect={(value) => handleStoreChange(value)}
            disabled={flowMounted}
            options={
              storesList?.map((store) => ({
                label: `${store.name} ${store.pj_id}`,
                value: store.id,
              })) ?? []
            }
          />
        </div>
        <div className='frame-input-wrapper'>
          <InputComponent
            onChange={() => {}}
            value={currentCreditsValue()}
            labelValue={PURCHASE_CREDITS_SCREEN.CURRENT_CREDITS}
            disabled
          />
        </div>
        <div className='frame-input-wrapper'>
          <InputComponent
            onChange={handleCreditsAmountChange}
            value={creditsAmount ?? '0'}
            labelValue={PURCHASE_CREDITS_SCREEN.CREDIT_AMOUNT_TO_BE_PURCHASED}
            numericInput
            disabled={flowMounted}
          />
          <TextComponent
            opacity
            size='extra-small'
            text={PURCHASE_CREDITS_SCREEN.CREDIT_VALUE}
          />
          <TextComponent opacity size='extra-small' text={getAmountToPay()} />
        </div>
        {/* Checkout Flow renders the card fields inside Checkout-hosted
            components (no card data touches our DOM). */}
        <div className='flow-container' ref={flowContainerRef} />
      </Col>
      <div className='payemnt-buttons-wrapper'>
        {!flowMounted && (
          <ButtonComponent
            appereance='primary'
            size='middle'
            label={PURCHASE_CREDITS_SCREEN.CONFIRM_AND_PAY}
            fullWidth
            onClick={handleContinueToPayment}
            disabled={!creditsAmount || creditsAmount === '0'}
          />
        )}
        <ButtonComponent
          appereance='secondary'
          size='middle'
          label={PURCHASE_CREDITS_SCREEN.CANCEL}
          fullWidth
          onClick={
            onClose ? () => onClose() : () => navigate('/home')
          }
        />
      </div>
    </div>
  );
};
